import { ActionAfterViewed } from '@cian/action-after-viewed-component';
import * as React from 'react';
import { useSelector } from 'react-redux';

import {
  trackBuilderButtonClick,
  trackBuilderLogoClick,
  trackNewbuildingClick,
  trackOfferClick,
  trackPromoBuilderOffersShow,
  trackSlideChange,
} from './tracking';
import { Carousel as NewbuildingPromoBuilderOffersCarousel } from '../../../packages/Carousel/Carousel';
import { NewbuildingPromoBuilder } from '../../components/NewbuildingPromoBuilder';
import { NewbuildingPromoBuilderOffersLayout } from '../../components/NewbuildingPromoBuilderOffersLayout';
import { NewbuildingPromoOfferCard } from '../../components/NewbuildingPromoOfferCard';
import { NEWBUILDING_PROMO_BUILDER_OFFERS_CAROUSEL_OPTIONS } from '../../constants/newbuildingPromoBuilderOffers';
import { getJsonQuery } from '../../selectors/getJsonQuery';
import { selectNewbuildingPromoBuilderOffers } from '../../selectors/newbuildingPromoBuilderOffers';
import { selectIsSerpFetching } from '../../selectors/selectIsSerpFetching';
import { INewbuildingPromoBuilderOffers } from '../../types/newbuildingPromoBuilderOffers';
import { selectSource } from 'shared/selectors';

export function NewbuildingPromoBuilderOffersContainer() {
  const newbuildingPromoBuilderOffers = useSelector(
    selectNewbuildingPromoBuilderOffers,
  ) as INewbuildingPromoBuilderOffers;
  const jsonQuery = useSelector(getJsonQuery);
  const source = useSelector(selectSource);
  const isFetching = useSelector(selectIsSerpFetching);

  const { allOffersUrl, builder, offers } = newbuildingPromoBuilderOffers;
  const { realtyUserId, cardUrl: builderCardUrl } = builder;

  const [shownSlides, setShownSlides] = React.useState<number[]>([]);

  const title = `Квартиры от застройщика ${builder.name}`;

  const onSlideChangeHandle = React.useCallback(
    (activeSlideIndex: number) => {
      const newSlides = offers
        .map(offer => offer.id)
        .slice(activeSlideIndex, activeSlideIndex + NEWBUILDING_PROMO_BUILDER_OFFERS_CAROUSEL_OPTIONS.slidesPerView);

      const isPossibleSendEvent = !!newSlides.filter(newSlide => !shownSlides.includes(newSlide)).length;

      /* istanbul ignore next */
      if (!isPossibleSendEvent) {
        return;
      }

      setShownSlides(oldSlides => [...new Set([...oldSlides, ...newSlides])]);

      trackSlideChange({
        activeSlideIndex,
        jsonQuery,
        realtyUserId,
        offers,
        source,
        title,
        builderCardUrl,
      });
    },
    [offers, realtyUserId, jsonQuery, source, title, shownSlides, builderCardUrl],
  );

  const slides = React.useMemo(() => {
    return offers.map((offer, index) => (
      <NewbuildingPromoOfferCard
        key={offer.id}
        {...offer}
        onOfferClick={() =>
          trackOfferClick({
            jsonQuery,
            realtyUserId,
            position: index,
            offerId: offer.id,
            source,
            cardUrl: offer.cardUrl,
            title,
          })
        }
        onNewbuilldingClick={() =>
          trackNewbuildingClick({
            jsonQuery,
            realtyUserId,
            position: index,
            newbuildingId: offer.newbuilding?.id,
            source,
            newbuildingUrl: offer.newbuilding?.url,
            title,
          })
        }
      />
    ));
  }, [offers, realtyUserId, jsonQuery, source, title]);

  const onAfterInitHandle = React.useCallback(() => {
    trackPromoBuilderOffersShow({
      jsonQuery,
      realtyUserId,
      offers,
      source,
      title,
      builderCardUrl,
    });

    const newSlides = offers
      .map(offer => offer.id)
      .slice(0, NEWBUILDING_PROMO_BUILDER_OFFERS_CAROUSEL_OPTIONS.slidesPerView);

    setShownSlides(newSlides);
  }, [jsonQuery, realtyUserId, offers, source, title, builderCardUrl]);

  const carousel = React.useMemo(() => {
    return (
      <NewbuildingPromoBuilderOffersLayout
        offersFewed={offers.length < NEWBUILDING_PROMO_BUILDER_OFFERS_CAROUSEL_OPTIONS.slidesPerView}
        builder={
          <NewbuildingPromoBuilder
            allOffersUrl={allOffersUrl}
            title={title}
            onLogoClick={() =>
              trackBuilderLogoClick({
                jsonQuery,
                realtyUserId,
                source,
                builderCardUrl,
                title,
              })
            }
            onButtonClick={() =>
              trackBuilderButtonClick({
                jsonQuery,
                realtyUserId,
                source,
                builderAllOffersUrl: allOffersUrl,
                title,
              })
            }
            {...builder}
          />
        }
        carousel={
          <NewbuildingPromoBuilderOffersCarousel
            onSlideChange={swiper => onSlideChangeHandle(swiper.activeIndex)}
            {...NEWBUILDING_PROMO_BUILDER_OFFERS_CAROUSEL_OPTIONS}
          >
            {slides}
          </NewbuildingPromoBuilderOffersCarousel>
        }
      />
    );
  }, [
    offers.length,
    allOffersUrl,
    builder,
    title,
    slides,
    jsonQuery,
    realtyUserId,
    source,
    builderCardUrl,
    onSlideChangeHandle,
  ]);

  /* istanbul ignore next */
  if (isFetching) {
    return carousel;
  }

  return (
    <ActionAfterViewed percentVisible={0.5} callback={onAfterInitHandle}>
      {carousel}
    </ActionAfterViewed>
  );
}
